import no from './languages/no.json';
import en from './languages/en.json';
import sv from './languages/sv.json';
import {debounce} from 'throttle-debounce';
import {pick} from 'dot-object';
import {getLanguage, DEFAULT_LANGUAGE} from '~/utils/language';

const strMap = {
  no,
  en,
  sv,
};

const debug = false;

export const MISSING_TRANSLATION = '[missing translation]';

const missingKeys = new Set();

const printMissing = debounce(60_000, () => {
  console.error(`Missing i18n for (lang: key):\n  ${[...missingKeys].join('\n  ')}`);
});

const fallbackKeys = new Set();

const printFallbacks = debounce(60_000, () => {
  console.warn(`i18n fell back to ${DEFAULT_LANGUAGE} for (lang: key):\n  ${[...fallbackKeys].join('\n  ')}`);
});

/**
 * Replaces {{param}} placeholders inside translation strings
 */
const interpolate = (value, params) => {
  if (typeof value !== 'string') return value;

  return value.replaceAll(/{{(\w+)}}/g, (_, key) => params[key] ?? `{{${key}}}`);
};

export const i18n = (key, params = {}) => {
  const lang = getLanguage();

  const target = strMap[lang] || strMap[DEFAULT_LANGUAGE];

  let strData = pick(key, target);

  // Fall back to the default language for keys not yet translated in `lang`
  // (e.g. editor strings while only the player is translated to Swedish).
  // Fallbacks are reported separately so untranslated keys stay visible
  // instead of silently rendering the default language.
  if (strData == null && target !== strMap[DEFAULT_LANGUAGE]) {
    strData = pick(key, strMap[DEFAULT_LANGUAGE]);

    if (strData != null) {
      fallbackKeys.add(`${lang}: ${key}`);
      printFallbacks();
    }
  }

  if (strData == null) {
    missingKeys.add(`${lang}: ${key}`);
    printMissing();

    return `${MISSING_TRANSLATION}-${key}`;
  }

  const result = interpolate(strData, params);

  if (debug) {
    return `..${result}`;
  }

  return result;
};
